import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import Button from './Button';

interface ConsultationFormProps {
  onClose: () => void;
}

const ConsultationForm: React.FC<ConsultationFormProps> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableSlots = [
    { day: 'Tuesday', slots: ['11:00 AM', '12:00 PM', '1:00 PM'] },
    { day: 'Thursday', slots: ['11:00 AM', '12:00 PM', '1:00 PM'] },
    { day: 'Friday', slots: ['9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'] }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('form-name', 'consultation');
      formDataObj.append('name', formData.name);
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('preferred_date', formData.preferred_date);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        toast.success('Consultation request submitted successfully!');
        onClose();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-abadi text-xl font-medium">Schedule Consultation</h3>
          <button onClick={onClose} className="text-dark-gray hover:text-dark">✕</button>
        </div>

        <form
          onSubmit={handleSubmit}
          name="consultation"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          className="space-y-4"
        >
          <input type="hidden" name="form-name" value="consultation" />
          <p className="hidden">
            <label>Don't fill this out: <input name="bot-field" /></label>
          </p>

          <div>
            <label className="block text-dark-gray mb-1">Name *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
            />
          </div>

          <div>
            <label className="block text-dark-gray mb-1">Email *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
            />
          </div>

          <div>
            <label className="block text-dark-gray mb-1">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
            />
          </div>

          <div>
            <label className="block text-dark-gray mb-1">Available Time Slots *</label>
            <input type="hidden" name="preferred_date" value={formData.preferred_date} />
            <div className="space-y-2">
              {availableSlots.map(({ day, slots }) => (
                <div key={day}>
                  <div className="font-medium mb-1">{day}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map(time => (
                      <label
                        key={`${day}-${time}`}
                        className={`
                          cursor-pointer rounded-md border p-2 text-center text-sm
                          ${formData.preferred_date === `${day} ${time}`
                            ? 'bg-[#f3fcae] border-[#f3fcae]'
                            : 'border-medium-gray hover:border-[#f3fcae]'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name="time_slot"
                          value={`${day} ${time}`}
                          checked={formData.preferred_date === `${day} ${time}`}
                          onChange={(e) => setFormData(prev => ({ ...prev, preferred_date: e.target.value }))}
                          className="sr-only"
                        />
                        {time}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Scheduling...' : 'Schedule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultationForm;
